/* Admin · Notification Approvals — the prototype's two-table layout with the email
 * preview modal. Drafts are created automatically by the pipeline; nothing is sent
 * until an admin approves, and the audience is computed from document access groups. */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { notifications } from '../../api/client'
import type { NotificationDetail, NotificationSummary } from '../../api/types'
import {
  Card,
  EmptyState,
  Kpi,
  PageHead,
  Skeleton,
  dateHuman,
  errorMessage,
  nf,
  useConfirm,
  useModal,
  useToast,
} from '../../ui/chrome'
import { Icon } from '../../ui/icons'
import { exportCSV } from '../../ui/pdf'

function statusPill(status: string) {
  switch (status) {
    case 'pending_approval':
    case 'draft':
      return <span className="pill p-warn">Awaiting approval</span>
    case 'approved':
      return <span className="pill p-info">Approved</span>
    case 'sent':
      return <span className="pill p-ok">Sent</span>
    case 'rejected':
      return <span className="pill p-danger">Rejected</span>
    default:
      return <span className="pill p-grey">{status}</span>
  }
}

/* The add-recipient box, as a component with its own state rather than markup inside the
 * modal's body.
 *
 * WHY IT CANNOT LIVE IN THE PARENT
 * `openModal` stores `spec.body` as an element in ModalProvider state (ui/chrome.tsx). That
 * element is built once, when the modal opens, and nothing rebuilds it on a parent re-render —
 * so a controlled input reading parent state was frozen at the value it had when the dialog
 * appeared: typing changed the state, the box still showed '', and `disabled={!draft.trim()}`
 * stayed true forever. Nobody could be added. State that a snapshot has to see has to live
 * inside the snapshot.
 */
function AddRecipient({ onAdd }: { onAdd: (upn: string) => void }) {
  const [draft, setDraft] = useState('')

  function submit() {
    const upn = draft.trim()
    if (!upn) return
    setDraft('')
    onAdd(upn)
  }

  return (
    <div className="recipient-add">
      <input
        className="inp"
        placeholder="name@kirloskar.com"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit()
        }}
      />
      <button type="button" className="btn btn-sm" disabled={!draft.trim()} onClick={submit}>
        Add
      </button>
    </div>
  )
}

export default function NotificationsScreen({ onChanged }: { onChanged: () => void }) {
  const toast = useToast()
  const confirm = useConfirm()
  const { openModal, closeModal } = useModal()

  const [items, setItems] = useState<NotificationSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(() => {
    notifications
      .list('all')
      .then((list) => {
        setItems(list)
        setError(null)
      })
      .catch((cause: unknown) => setError(errorMessage(cause)))
  }, [])

  useEffect(refresh, [refresh])

  const pending = useMemo(
    () => (items ?? []).filter((n) => n.status === 'pending_approval' || n.status === 'draft'),
    [items],
  )
  const history = useMemo(
    () => (items ?? []).filter((n) => n.status !== 'pending_approval' && n.status !== 'draft'),
    [items],
  )
  const sentCount = history.filter((n) => n.status === 'sent').length
  const approvedCount = history.filter((n) => n.status === 'approved').length
  const rejectedCount = history.filter((n) => n.status === 'rejected').length

  function decide(n: NotificationSummary, decision: 'approve' | 'reject', reason?: string) {
    notifications
      .decide(n.id, decision === 'approve' ? { decision } : { decision, rejected_reason: reason })
      .then(() => {
        refresh()
        onChanged()
        toast(
          'ok',
          decision === 'approve' ? 'Notification approved' : 'Notification rejected',
          decision === 'approve'
            ? 'Nothing has been sent yet — use Send now when you are ready.'
            : n.id,
        )
      })
      .catch((cause: unknown) => toast('err', 'Decision failed', errorMessage(cause)))
  }

  /* Editable until something has actually gone out. After that the list is a delivery record
   * rather than a plan, and changing it would erase who was mailed. */
  function editableStatus(status: string) {
    return status === 'draft' || status === 'pending_approval' || status === 'approved'
  }

  function addRecipient(id: string, upn: string) {
    notifications
      .addRecipient(id, upn)
      .then((updated) => {
        refresh()
        openPreview(updated)
        toast('ok', 'Recipient added', upn)
      })
      .catch((cause: unknown) => toast('err', 'Could not add recipient', errorMessage(cause)))
  }

  function refreshRecipients(id: string) {
    notifications
      .refreshRecipients(id)
      .then((updated) => {
        refresh()
        openPreview(updated)
        toast(
          'ok',
          'Audience rebuilt',
          `${nf(updated.actual_recipient_count)} people can read this document.`,
        )
      })
      .catch((cause: unknown) => toast('err', 'Could not rebuild the audience', errorMessage(cause)))
  }

  function removeRecipient(id: string, recipientId: string, email: string) {
    notifications
      .removeRecipient(id, recipientId)
      .then((updated) => {
        refresh()
        openPreview(updated)
        toast('ok', 'Recipient removed', email)
      })
      .catch((cause: unknown) => toast('err', 'Could not remove recipient', errorMessage(cause)))
  }

  /* Separate from approval on purpose: approving says the copy is right, sending is the step
   * that cannot be undone. A reviewer working through five drafts should not find they have
   * mailed the company five times. */
  function sendBatch(n: NotificationSummary) {
    /* Fetch the list first. "412 recipients" is a number somebody has to take on trust before an
     * action that cannot be undone; the addresses are something they can read. */
    notifications
      .get(n.id)
      .then((detail) => confirmSend(detail))
      .catch((cause: unknown) => toast('err', 'Could not load recipients', errorMessage(cause)))
  }

  /* Approved is the first send; failed is a re-send after a relay outage. Both go through the
   * same button, and the server skips anyone already delivered either way. */
  function sendableStatus(status: string) {
    return status === 'approved' || status === 'failed'
  }

  /* A batch can read `sent` and still have somebody the relay refused — a full mailbox, a
   * greylisted address. Those are visible in the detail, so the retry is offered there. */
  function hasUndelivered(n: NotificationDetail) {
    return n.recipients.some((r) => r.delivery_status !== 'sent')
  }

  function confirmSend(n: NotificationDetail) {
    /* On a retry the people to mail are the ones the relay refused as well as the ones it never
     * reached — the server returns `failed` rows to `pending` when it claims the batch, so a
     * dialog counting only `pending` would promise nought recipients and then mail four. */
    const pendingList = n.recipients.filter((r) => r.delivery_status !== 'sent')
    confirm(
      'Send this notification now?',
      <>
        <p>
          “{n.subject}” will be emailed to{' '}
          <strong>
            {nf(pendingList.length)} recipient{pendingList.length === 1 ? '' : 's'}
          </strong>
          . This cannot be undone.
        </p>
        {pendingList.length > 0 && (
          <div className="recipient-list" style={{ marginTop: 10 }}>
            {pendingList.map((r) => (
              <div key={r.id} className="recipient-row">
                <span className="tiny">{r.recipient_email}</span>
                <span className="tiny muted">{r.group_name ?? 'Unattributed'}</span>
              </div>
            ))}
          </div>
        )}
      </>,
      n.status === 'approved' ? 'Send now' : 'Retry send',
      () => {
        notifications
          .send(n.id)
          .then(() => {
            refresh()
            onChanged()
            toast('ok', 'Sending started', 'Delivery is running; the status will update.')
          })
          .catch((cause: unknown) => toast('err', 'Could not send', errorMessage(cause)))
      },
    )
  }

  function approveBatch(n: NotificationSummary) {
    confirm(
      'Approve this notification?',
      `“${n.subject}” will be marked ready for ${nf(n.actual_recipient_count)} recipient${
        n.actual_recipient_count === 1 ? '' : 's'
      }. Nothing is emailed until you press Send now.`,
      'Approve',
      () => decide(n, 'approve'),
    )
  }

  function rejectBatch(n: NotificationSummary) {
    let reason = ''
    openModal({
      title: 'Reject notification',
      sub: n.subject,
      size: 'narrow',
      body: (
        <div className="field">
          <label htmlFor="rjNote">
            Reason for rejection <span className="req">*</span>
          </label>
          <textarea
            id="rjNote"
            className="inp"
            placeholder="Why should this not be sent?"
            onChange={(event) => {
              reason = event.target.value
            }}
          />
        </div>
      ),
      footer: (
        <>
          <button type="button" className="btn btn-soft" onClick={closeModal}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => {
              if (reason.trim().length < 8) {
                toast('err', 'Reason required', 'Describe the rejection in at least 8 characters.')
                return
              }
              closeModal()
              decide(n, 'reject', reason)
            }}
          >
            Reject
          </button>
        </>
      ),
    })
  }

  function preview(n: NotificationSummary) {
    notifications
      .get(n.id)
      .then(openPreview)
      .catch((cause: unknown) => toast('err', 'Preview unavailable', errorMessage(cause)))
  }

  /* Takes the detail rather than fetching it, so an add or a remove can redraw the open modal
   * from the response that call already returned — one round trip, and no flicker. */
  function openPreview(detail: NotificationDetail) {
    {
      {
        const n: NotificationSummary = detail
        const isPending = detail.status === 'pending_approval' || detail.status === 'draft'
        openModal({
          /* The document, not the batch UUID. Nobody recognises an id, and the reviewer is
             deciding about a document. The id stays in the Batch detail panel for support. */
          title: `Email preview — ${detail.document_name}`,
          sub: detail.subject,
          size: 'wide',
          body: (
            <div className="grid g-2-1">
              <div className="email-preview">
                <div className="ep-meta">
                  <div>
                    <strong>From:</strong> KBL Internal AI Assistant &lt;no-reply@kbl.co.in&gt;
                  </div>
                  <div>
                    <strong>To:</strong>{' '}
                    {/* The list, not `recipient_count` — that field is written after delivery
                        and can disagree with who is actually on the list. */}
                    {nf(detail.actual_recipient_count)} recipient
                    {detail.actual_recipient_count === 1 ? '' : 's'}
                    {detail.recipient_breakdown.length > 0 &&
                      ` (${detail.recipient_breakdown.map((g) => g.group_name).join(', ')})`}
                  </div>
                  <div>
                    <strong>Subject:</strong> {detail.subject}
                  </div>
                  <div>
                    <strong>Channel:</strong> Email (SMTP relay provided by KBL)
                  </div>
                </div>
                <div className="ep-body">
                  {/* The draft body is server-rendered HTML reviewed by this very screen. */}
                  <div dangerouslySetInnerHTML={{ __html: detail.html_body }} />
                </div>
              </div>

              <div>
                <div className="card" style={{ marginBottom: 16 }}>
                  <div className="card-h">
                    <h3>Recipients</h3>
                    {editableStatus(detail.status) ? (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        title="Rebuild from who can read this document now"
                        onClick={() => refreshRecipients(detail.id)}
                      >
                        <Icon name="refresh" size={14} /> Rebuild
                      </button>
                    ) : (
                      <span className="tiny muted">{nf(detail.actual_recipient_count)}</span>
                    )}
                  </div>
                  <div className="card-b">
                    {detail.recipients.length === 0 ? (
                      <p className="tiny muted">Nobody is on this list yet.</p>
                    ) : (
                      <div className="recipient-list">
                        {detail.recipients.map((r) => (
                          <div key={r.id} className="recipient-row">
                            <div>
                              <div className="tiny">{r.recipient_email}</div>
                              <div className="tiny muted">
                                {r.group_name ?? 'Unattributed'}
                                {r.delivery_status !== 'pending' && ` · ${r.delivery_status}`}
                              </div>
                            </div>
                            {editableStatus(detail.status) && (
                              <button
                                type="button"
                                className="btn-icon"
                                title="Remove from this alert"
                                onClick={() => removeRecipient(detail.id, r.id, r.recipient_email)}
                              >
                                <Icon name="x" size={14} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {editableStatus(detail.status) && (
                      <AddRecipient onAdd={(upn) => addRecipient(detail.id, upn)} />
                    )}
                    {editableStatus(detail.status) && (
                      <p className="tiny muted" style={{ marginTop: 8 }}>
                        Only people who can already read this document can be added.
                      </p>
                    )}
                  </div>
                </div>

                <div className="card">
                  <div className="card-h">
                    <h3>Batch detail</h3>
                  </div>
                  <div className="card-b">
                    {[
                      ['Source document', detail.document_name],
                      // Out of the title, but still here: support asks for it by id.
                      ['Batch id', <span className="mono tiny">{detail.id}</span>],
                      ['Trigger', detail.change_type === 'new' ? 'New document' : 'Updated document'],
                      ['Created', dateHuman(detail.created_at)],
                      ['Status', statusPill(detail.status)],
                      ['Approver', detail.approved_by ?? 'Pending'],
                      ['Sent at', detail.sent_at ? dateHuman(detail.sent_at) : '—'],
                    ].map(([k, v]) => (
                      <div key={String(k)} className="stat-line">
                        <span className="sl-k">{k}</span>
                        <span className="sl-v">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="card" style={{ marginBottom: 0 }}>
                  <div className="card-h">
                    <h3>
                      <Icon name="users" size={16} /> Computed audience
                    </h3>
                  </div>
                  <div className="card-b">
                    {detail.recipient_breakdown.map((g) => (
                      <div key={g.group_name} className="stat-line">
                        <span className="sl-k">{g.group_name}</span>
                        <span className="sl-v">{nf(g.recipient_count)}</span>
                      </div>
                    ))}
                    <div className="stat-line">
                      <span className="sl-k">
                        <strong>Total recipients</strong>
                      </span>
                      <span className="sl-v">
                        <strong>{nf(detail.actual_recipient_count)}</strong>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ),
          footer: (
            <>
              <button type="button" className="btn btn-soft" onClick={closeModal}>
                Close
              </button>
              {(sendableStatus(detail.status) ||
                (detail.status === 'sent' && hasUndelivered(detail))) && (
                <button
                  type="button"
                  className="btn btn-lime"
                  onClick={() => {
                    closeModal()
                    sendBatch(n)
                  }}
                >
                  {detail.status === 'approved' ? 'Send now' : 'Retry send'}
                </button>
              )}
              {isPending && (
                <>
                  <button
                    type="button"
                    className="btn btn-soft"
                    onClick={() => {
                      closeModal()
                      rejectBatch(n)
                    }}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    className="btn btn-lime"
                    onClick={() => {
                      closeModal()
                      approveBatch(n)
                    }}
                  >
                    Approve
                  </button>
                </>
              )}
            </>
          ),
        })
      }
    }
  }

  function row(n: NotificationSummary) {
    const isPending = n.status === 'pending_approval' || n.status === 'draft'
    return (
      <tr key={n.id}>
        <td className="mono tiny">{n.id.slice(0, 8)}</td>
        <td>
          <strong>{n.subject}</strong>
          <div className="tiny muted">
            {n.document_name} · {n.change_type === 'new' ? 'New document' : 'Updated document'}
          </div>
        </td>
        {/* The list, not the post-delivery figure. A row that has been sent shows what was
            actually delivered instead, which is the number that matters afterwards. */}
        <td className="num">
          {n.status === 'sent' && n.recipient_count !== null
            ? nf(n.recipient_count)
            : nf(n.actual_recipient_count)}
        </td>
        <td>{statusPill(n.status)}</td>
        <td>
          {n.approved_by ?? '—'}
          {n.sent_at && <div className="muted tiny">{dateHuman(n.sent_at)}</div>}
        </td>
        <td>
          <span className="tiny nowrap">{dateHuman(n.created_at)}</span>
        </td>
        <td>
          <div className="actions">
            <button type="button" className="btn-icon" title="Preview email" onClick={() => preview(n)}>
              <Icon name="eye" size={15} />
            </button>
            {isPending && (
              <>
                <button type="button" className="btn btn-xs btn-lime" onClick={() => approveBatch(n)}>
                  Approve
                </button>
                <button type="button" className="btn btn-xs btn-soft" onClick={() => rejectBatch(n)}>
                  Reject
                </button>
              </>
            )}
            {sendableStatus(n.status) && (
              <button type="button" className="btn btn-xs btn-lime" onClick={() => sendBatch(n)}>
                {n.status === 'failed' ? 'Retry send' : 'Send now'}
              </button>
            )}
          </div>
        </td>
      </tr>
    )
  }

  const head = (
    <tr>
      <th>Batch</th>
      <th>Subject</th>
      <th className="num">Recipients</th>
      <th>Status</th>
      <th>Approver</th>
      <th>Created</th>
      <th style={{ textAlign: 'right' }}>Actions</th>
    </tr>
  )

  return (
    <>
      <PageHead
        title="Notification Approvals"
        actions={
          <button
            type="button"
            className="btn btn-soft"
            disabled={!items || items.length === 0}
            onClick={() => {
              exportCSV('kbl-notification-log.csv', [
                ['Batch', 'Document', 'Type', 'Subject', 'Recipients', 'Status', 'Approver', 'Created', 'Sent at'],
                ...(items ?? []).map((n) => [
                  n.id,
                  n.document_name,
                  n.change_type,
                  n.subject,
                  n.recipient_count ?? '',
                  n.status,
                  n.approved_by ?? '',
                  n.created_at,
                  n.sent_at ?? '',
                ]),
              ])
              toast('ok', 'Export complete', `kbl-notification-log.csv downloaded (${items?.length ?? 0} rows).`)
            }}
          >
            <Icon name="dl" size={16} /> Export log
          </button>
        }
      />

      <div className="grid g4 mb16">
        <Kpi tone="warn" label="Awaiting approval" value={nf(pending.length)} hoverDesc="Waiting for your approval before sending" />
        <Kpi tone="info" label="Approved" value={nf(approvedCount)} hoverDesc="Approved and will be sent shortly" />
        <Kpi tone="green" label="Sent" value={nf(sentCount)} hoverDesc="Emails already delivered" />
        <Kpi tone="danger" label="Rejected" value={nf(rejectedCount)} hoverDesc="Rejected and will not be sent" />
      </div>

      {error && (
        <div className="alert a-danger">
          <Icon name="alert" size={18} />
          <div>
            <strong>Notifications could not be loaded.</strong>
            {error}
          </div>
        </div>
      )}

      <Card icon="mail" title="Awaiting your approval" hint={String(pending.length)} tight className="mb16">
        {items === null ? (
          <Skeleton rows={3} />
        ) : pending.length === 0 ? (
          <EmptyState
            icon="checkCircle"
            title="Nothing awaiting approval"
            msg="Drafts appear here when the pipeline detects a new or updated document."
          />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>{head}</thead>
              <tbody>{pending.map(row)}</tbody>
            </table>
          </div>
        )}
      </Card>

      <Card icon="list" title="Delivery log" hint={String(history.length)} tight>
        {items === null ? (
          <Skeleton rows={3} />
        ) : history.length === 0 ? (
          <EmptyState icon="mail" title="No delivery history" msg="" />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>{head}</thead>
              <tbody>{history.map(row)}</tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  )
}
